import {
  BackComponent,
  CheckoutPriceCardBase,
  Column,
  LineProgress,
  MobileCheckoutPriceCardBase,
  Row,
  SecondaryPageMainContent,
  SecondaryPageWidgetContainer,
  ShowWhen,
  Typography,
} from '@/components'
import { Breakpoint, ComponentColors, ThemeColor } from '@/enums'
import { SureinLogoNew } from '@/icons'
import { useSegment } from '@/modules/analytics'
import { CheckoutStepKeys } from '@/pages/CheckoutPage/steps'
import { useContactModal } from '@/providers'
import { theme } from '@/theme'
import {
  themeBreakpointDown,
  themeColor,
  useGoToPath,
  useIsMobile,
  useToggable,
} from '@/utils'
import { Button } from '@surein/ui'
import { ArrowRight, Calendar, FileText, Lock, Phone } from 'lucide-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { CheckoutTrustPartners } from './CheckoutTrustPartners'
import { paths } from '@/routes/paths'

const StepperContainer = styled(Column)`
  gap: 24px;
  padding: 16px 24px;
  flex: 1;
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }
`

const StepWrapper = styled(Column)`
  gap: 24px;
  flex: 1;
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }
`

const TopElementsWrapper = styled(Column)`
  max-width: 200px;
  gap: 24px;

  > div {
    flex: 2;
  }

  > div:nth-child(2) {
    flex: 6;
  }

  ${themeBreakpointDown(Breakpoint.sm)} {
    > div {
      flex: unset;
    }
  }
`

const StyledSecondaryPageMainContent = styled(SecondaryPageMainContent)`
  align-self: auto;
  width: 100%;
  margin: 48px;
  @media (max-width: 900px) {
    margin: 0 !important;
    flex: 1;
    height: calc(100vh - 60px);
    height: calc(100svh - 60px);
  }
  border: 1px solid ${({ theme }) => theme.color.b30};

  ${themeBreakpointDown(Breakpoint.tab)} {
    margin: 0;
  }
`

const CheckoutStepperActions = styled(Row)`
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid ${themeColor(ThemeColor.b30)};
  z-index: 2;
  background-color: white;
  z-index: 9000;
  position: relative;
  border-radius: 0 0 24px 24px;

  > button:first-child {
    padding: 8px 16px 8px 8px;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    border-top: none;
    border-radius: 0;
    padding: 12px 16px;
  }
`

const StepHeader = styled(Column)`
  gap: 8px;
`

const PriceCardContainer = styled(Column)`
  > div {
    padding-left: 0px;
  }
`

const WidgetContainer = styled(Column)`
  height: 100%;
  justify-content: space-between;
  padding-bottom: 8px;

  > div:last-child {
    padding-bottom: 24px;
  }
`

const TrustPartnersContainer = styled(Column)`
  margin-top: 24px;
`

const PageWidgetContainer = styled(SecondaryPageWidgetContainer)`
  align-self: center;
  height: calc(100vh - 80px);
  height: calc(100svh - 80px);
  overflow-y: auto;
`

const MobileButtonsContainer = styled(Row)<{ isKillerQuestionsStep: boolean }>`
  gap: 8px;
  width: 100%;

  > button {
    width: ${({ isKillerQuestionsStep }) =>
      !isKillerQuestionsStep ? '100%' : 'auto'};
    flex: 1;
  }
`

const StyledBackComponent = styled(BackComponent)`
  button {
    padding: 0;
  }
`

export interface CheckoutStep {
  key: string
  keyIndex: number
  Component: React.ComponentType
  title?: string
  subtitle: string
  breadcrumbs?: string
  tooltip?: string
}

type CheckoutStepState = {
  index: number
  step: string
  data: {
    title?: string
    subtitle?: string
    tooltip?: React.ReactNode
  }
  isFirst: boolean
  isLast: boolean
  back: () => void
}

export type ButtonState = {
  disabled?: boolean
  loading?: boolean
  text?: string
  onClick: () => void
}

interface Props {
  buttonState: ButtonState
  secondaryButtonState: ButtonState
  children: React.ReactNode
  data: any[]
  isPublic?: boolean
  steps: CheckoutStep[]
  stepState: CheckoutStepState
}

const PhoneIcon = () => <Phone color={theme.color.success1} size={16} />

export const CheckoutBase = ({
  steps,
  data,
  buttonState,
  secondaryButtonState,
  stepState,
  children,
  isPublic,
}: Props) => {
  const { t } = useTranslation()
  const { state } = useLocation()
  const { backPathname } = state || {}
  const { openContact } = useContactModal()
  const { width } = useIsMobile()
  const isMobile = width <= 900
  const [isCheckoutDetailsOpen, setCheckoutDetailsOpen, toggle] = useToggable(
    false,
  )

  const analyticsPrefix = isPublic ? 'ssf' : 'app'

  const handleBack = () => {
    if (stepState.isFirst && backPathname) {
      window.history.back()
    } else {
      stepState.back()
    }
  }

  const handleClickNext = () => {
    setCheckoutDetailsOpen(false)
    buttonState.onClick()
  }

  const goToRoot = useGoToPath(paths.authRedirect)

  const analytics = useSegment()

  useEffect(() => {
    analytics.page({
      name: 'Page View',
      page_name: `${analyticsPrefix}_${stepState.step}`,
    })
  }, [stepState.step])

  const isKillerQuestionsStep =
    stepState.step === CheckoutStepKeys.killerQuestions

  return (
    <>
      <StyledSecondaryPageMainContent heightOffset={96}>
        <StepperContainer>
          <StepWrapper>
            <Column gap="16px">
              {!isMobile && (
                <Row justifyContent="space-between" alignItems="center">
                  <Button
                    variant="ghost"
                    onClick={() => goToRoot()}
                    style={{ padding: 0, background: 'transparent' }}
                  >
                    <SureinLogoNew height={24} width={88} />
                  </Button>
                  <Button
                    size="sm"
                    onClick={openContact}
                    variant="muted"
                    icon={PhoneIcon}
                  />
                </Row>
              )}
              <TopElementsWrapper>
                {!isPublic || !stepState.isFirst || backPathname ? (
                  <StyledBackComponent
                    noContentGutter
                    onBack={!stepState.isFirst ? handleBack : undefined}
                  />
                ) : (
                  <div />
                )}
                <LineProgress
                  percentage={((stepState.index + 1) / steps.length) * 100}
                  color={ComponentColors.primary}
                  height="8px"
                />
              </TopElementsWrapper>
            </Column>
            {isMobile && (
              <MobileCheckoutPriceCardBase
                data={data}
                isCheckoutDetailsOpen={isCheckoutDetailsOpen}
                toggle={toggle}
              />
            )}
            {stepState.data.title && (
              <StepHeader gap="8px">
                <Row gap="8px">
                  <Typography bold variant="h4">
                    {stepState.data.title}
                  </Typography>
                  {stepState.data?.tooltip}
                </Row>
                <ShowWhen when={Boolean(stepState.data.subtitle)}>
                  <Typography color={ThemeColor.b50} variant="p2Body">
                    {stepState.data.subtitle}
                  </Typography>
                </ShowWhen>
              </StepHeader>
            )}
            {children}
          </StepWrapper>
        </StepperContainer>
        <CheckoutStepperActions>
          <ShowWhen when={!isMobile}>
            {stepState.step === CheckoutStepKeys.killerQuestions ? (
              <Button
                onClick={secondaryButtonState?.onClick}
                variant="muted"
                shape="square"
                disabled={secondaryButtonState?.disabled}
                loading={secondaryButtonState?.loading}
              >
                {secondaryButtonState?.text}
              </Button>
            ) : (
              <div />
            )}
            <Button
              disabled={buttonState.disabled}
              loading={buttonState.loading}
              onClick={handleClickNext}
              reverted
              variant="primary"
              shape="square"
              suffixIcon={ArrowRight}
            >
              {stepState.isLast ? t('confirmOrder') : buttonState.text}
            </Button>
          </ShowWhen>
          <ShowWhen when={isMobile}>
            <MobileButtonsContainer
              isKillerQuestionsStep={isKillerQuestionsStep}
            >
              {stepState.step === CheckoutStepKeys.killerQuestions ? (
                <Button
                  onClick={secondaryButtonState?.onClick}
                  variant="muted"
                  shape="square"
                  disabled={secondaryButtonState?.disabled}
                  loading={secondaryButtonState?.loading}
                >
                  {secondaryButtonState?.text}
                </Button>
              ) : (
                <div />
              )}
              <Button
                disabled={buttonState.disabled || buttonState.loading}
                loading={buttonState.loading}
                onClick={handleClickNext}
                reverted
                variant="primary"
                shape="square"
                suffixIcon={ArrowRight}
              >
                {stepState.isLast && !isKillerQuestionsStep
                  ? t('confirmOrder')
                  : buttonState.text}
              </Button>
            </MobileButtonsContainer>
            {!isKillerQuestionsStep ? (
              <Typography variant="p2Body" color={ThemeColor.success1}>
                {t('checkout.noRisk')}
              </Typography>
            ) : (
              <div />
            )}
          </ShowWhen>
        </CheckoutStepperActions>
      </StyledSecondaryPageMainContent>
      {!isMobile && (
        <PageWidgetContainer>
          <ShowWhen when={!isMobile}>
            <WidgetContainer>
              <Column gap="32px">
                <PriceCardContainer>
                  <CheckoutPriceCardBase
                    buttonText={buttonState.text}
                    data={data}
                  />
                </PriceCardContainer>
                <Column gap="8px">
                  <Row gap="8px" alignItems="center">
                    <Calendar size={16} color={theme.color.success1} />
                    <Typography
                      variant="description"
                      color={ThemeColor.success1}
                    >
                      {t('checkout.trustTextEntries.trust1')}
                    </Typography>
                  </Row>
                  <Row gap="8px" alignItems="center">
                    <FileText size={16} />
                    <Typography variant="description">
                      {t('checkout.trustTextEntries.trust2')}
                    </Typography>
                  </Row>
                  <Row gap="8px" alignItems="center">
                    <Lock size={16} />
                    <Typography variant="description">
                      {t('checkout.trustTextEntries.trust3')}
                    </Typography>
                  </Row>
                </Column>
              </Column>
              <TrustPartnersContainer>
                <CheckoutTrustPartners />
              </TrustPartnersContainer>
            </WidgetContainer>
          </ShowWhen>
        </PageWidgetContainer>
      )}
    </>
  )
}
